import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import dayjs from "dayjs";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  Cell, CartesianGrid
} from "recharts";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

const STATUS_STYLE = {
  APPROVED:  "text-[#00ff90] bg-[#00ff90]/10 border-[#00ff90]/20",
  PENDING:   "text-yellow-400 bg-yellow-500/10 border-yellow-500/20",
  REJECTED:  "text-red-400 bg-red-500/10 border-red-500/20",
  CANCELLED: "text-white/30 bg-white/5 border-white/10",
};

const BALANCE_COLORS = {
  annual:    "#00ff90",
  sick:      "#f87171",
  casual:    "#c084fc",
  unpaid:    "#fbbf24",
  maternity: "#60a5fa",
  paternity: "#34d399",
};

const fmt = d => d ? dayjs(d).format("DD MMM YYYY") : "—";

const tooltipStyle = {
  contentStyle: { background: "#0d0d0d", border: "1px solid #00ff9033", borderRadius: 8, fontSize: 11 },
  labelStyle: { color: "#00ff90" },
};

export default function MemberLeaveDrawer({ member, onClose }) {
  const [detail,  setDetail]  = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!member?.userId && !member?.id) return;
    const id = member.userId ?? member.id;
    const token = localStorage.getItem("token");
    setLoading(true);
    fetch(`${API_BASE}/api/manager/employee/${id}/leave`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => setDetail(d))
      .catch(() => setDetail(null))
      .finally(() => setLoading(false));
  }, [member?.userId, member?.id]);

  if (!member) return null;

  const balances    = detail?.balances    || [];
  const history     = detail?.history     || [];
  const conflicts   = detail?.conflicts   || [];
  const monthlyUsage = detail?.monthlyUsage || []; // [{month, days}]

  // Build balance bar chart data
  const balanceChartData = balances.map(b => ({
    type:      (b.type || "").charAt(0).toUpperCase() + (b.type || "").slice(1),
    Remaining: Math.max(0, (b.total ?? 0) - (b.used ?? 0)),
    Used:      b.used ?? 0,
    color:     BALANCE_COLORS[(b.type || "").toLowerCase()] || "#00ff90",
  }));

  // Upcoming approved leaves
  const today = dayjs();
  const upcoming = history
    .filter(h => h.status === "APPROVED" && dayjs(h.end_date || h.to).isAfter(today))
    .sort((a, b) => dayjs(a.start_date || a.from).diff(dayjs(b.start_date || b.from)))
    .slice(0, 3);

  return (
    <div className="fixed inset-0 z-[10060]">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      <div className="absolute right-0 top-0 h-full w-full sm:w-[480px] bg-[#0d0d0d] border-l border-[#00ff90]/20 shadow-[-20px_0_60px_#00ff9011] text-white flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5 shrink-0">
          <div>
            <p className="font-semibold text-white">{member.name}</p>
            <p className="text-xs text-[#00ff90]/50 mt-0.5">{member.role}</p>
          </div>
          <button onClick={onClose}
            className="w-8 h-8 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-white/40 hover:text-white transition-colors">
            <X size={14} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {loading ? (
            <div className="space-y-3">{[1,2,3,4].map(i => <div key={i} className="h-24 rounded-xl bg-white/5 animate-pulse" />)}</div>
          ) : (
            <>
              {/* Balance overview — stat cards */}
              {balances.length > 0 && (
                <div className="rounded-xl border border-[#00ff90]/15 bg-[#00ff90]/5 p-4">
                  <p className="text-xs text-[#00ff90]/50 uppercase tracking-wider mb-3">Leave Balances</p>
                  <div className="grid grid-cols-2 gap-2">
                    {balances.map((b, i) => {
                      const color = BALANCE_COLORS[(b.type || "").toLowerCase()] || "#00ff90";
                      const remaining = (b.total ?? 0) - (b.used ?? 0);
                      const pct = b.total > 0 ? Math.round((remaining / b.total) * 100) : 0;
                      return (
                        <div key={i} className="rounded-lg border border-white/5 bg-black/30 p-3">
                          <p className="text-xs text-white/30 capitalize mb-1">{b.type}</p>
                          <p className="text-xl font-bold" style={{ color }}>{remaining}</p>
                          <div className="h-1 rounded-full bg-white/5 mt-1.5">
                            <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
                          </div>
                          <p className="text-xs text-white/20 mt-1">{b.used} used / {b.total} total</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Balance remaining vs used — bar chart */}
              {balanceChartData.length > 0 && (
                <div className="rounded-xl border border-white/5 bg-white/3 p-4">
                  <p className="text-xs text-[#00ff90]/50 uppercase tracking-wider mb-3">Remaining vs Used</p>
                  <ResponsiveContainer width="100%" height={120}>
                    <BarChart data={balanceChartData} layout="vertical" margin={{ top: 0, right: 10, left: 0, bottom: 0 }}>
                      <XAxis type="number" tick={{ fontSize: 9, fill: "rgba(255,255,255,0.25)" }} />
                      <YAxis type="category" dataKey="type" tick={{ fontSize: 10, fill: "rgba(255,255,255,0.5)" }} width={55} />
                      <Tooltip {...tooltipStyle} formatter={(v, n) => [`${v} days`, n]} />
                      <Bar dataKey="Remaining" stackId="a" radius={[0,0,0,0]}>
                        {balanceChartData.map((d, i) => <Cell key={i} fill={d.color} fillOpacity={0.7} />)}
                      </Bar>
                      <Bar dataKey="Used" stackId="a" fill="rgba(255,255,255,0.1)" radius={[0,3,3,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                  <div className="flex gap-4 mt-2">
                    <span className="flex items-center gap-1.5 text-xs text-white/30"><span className="w-2 h-2 rounded-sm bg-[#00ff90]/60" />Remaining</span>
                    <span className="flex items-center gap-1.5 text-xs text-white/30"><span className="w-2 h-2 rounded-sm bg-white/20" />Used</span>
                  </div>
                </div>
              )}

              {/* Monthly leave usage */}
              {monthlyUsage.length > 0 && (
                <div className="rounded-xl border border-white/5 bg-white/3 p-4">
                  <p className="text-xs text-[#00ff90]/50 uppercase tracking-wider mb-3">Monthly Leave Taken (This Year)</p>
                  <ResponsiveContainer width="100%" height={90}>
                    <BarChart data={monthlyUsage} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
                      <CartesianGrid stroke="rgba(255,255,255,0.04)" />
                      <XAxis dataKey="month" tick={{ fontSize: 9, fill: "rgba(255,255,255,0.25)" }} />
                      <YAxis tick={{ fontSize: 9, fill: "rgba(255,255,255,0.25)" }} allowDecimals={false} />
                      <Tooltip {...tooltipStyle} formatter={v => [`${v} days`, "Leave taken"]} />
                      <Bar dataKey="days" fill="#00ff90" fillOpacity={0.6} radius={[3,3,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Upcoming leave */}
              {upcoming.length > 0 && (
                <div className="rounded-xl border border-blue-500/15 bg-blue-500/5 p-4">
                  <p className="text-xs text-blue-400/60 uppercase tracking-wider mb-3">Upcoming Leave</p>
                  <ul className="space-y-2">
                    {upcoming.map((h, i) => {
                      const start = dayjs(h.start_date || h.from);
                      const end   = dayjs(h.end_date   || h.to);
                      const daysUntil = start.diff(today, "day");
                      return (
                        <li key={i} className="flex items-center justify-between gap-2">
                          <div>
                            <span className="text-sm text-white/70 capitalize">{h.leave_type || h.type}</span>
                            <p className="text-xs text-white/30">{fmt(h.start_date || h.from)} → {fmt(h.end_date || h.to)}</p>
                          </div>
                          <span className="text-xs text-blue-400 bg-blue-500/10 border border-blue-500/20 px-2 py-0.5 rounded-full shrink-0">
                            {daysUntil === 0 ? "Today" : `In ${daysUntil}d`}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {/* Recent history */}
              <div className="rounded-xl border border-white/5 bg-white/3 p-4">
                <p className="text-xs text-[#00ff90]/50 uppercase tracking-wider mb-3">Recent Requests</p>
                {history.length > 0 ? (
                  <ul className="space-y-2">
                    {history.slice(0, 5).map((h, i) => (
                      <li key={i} className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <span className="text-sm text-white/70 capitalize">{h.leave_type || h.type}</span>
                          <p className="text-xs text-white/30">{fmt(h.start_date || h.from)} → {fmt(h.end_date || h.to)}</p>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-full border shrink-0 ${STATUS_STYLE[h.status] || "text-white/30 bg-white/5 border-white/10"}`}>
                          {h.status}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-white/20 text-sm italic">No recent requests.</p>
                )}
              </div>

              {/* Conflicts */}
              {conflicts.length > 0 && (
                <div className="rounded-xl border border-red-500/20 bg-red-500/5 p-4">
                  <p className="text-xs text-red-400/60 uppercase tracking-wider mb-3">Overlaps / Conflicts</p>
                  <ul className="space-y-1">
                    {conflicts.map((c, i) => (
                      <li key={i} className="text-sm text-red-400/70">{c.name} · {fmt(c.from)} – {fmt(c.to)}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Actions */}
              <div className="rounded-xl border border-white/5 bg-white/3 p-4">
                <p className="text-xs text-[#00ff90]/50 uppercase tracking-wider mb-3">Quick Actions</p>
                <div className="flex gap-2 flex-wrap">
                  <button className="text-xs px-3 py-1.5 rounded-lg border border-white/10 bg-white/5 text-white/50 hover:bg-white/10 transition-colors">Add Note</button>
                  <button className="text-xs px-3 py-1.5 rounded-lg border border-white/10 bg-white/5 text-white/50 hover:bg-white/10 transition-colors">Export</button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}